<template>
  <Card
    class="CardTravelerPersona"
    tag="article"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <div class="CardTravelerPersona-image" aria-hidden="true">
      <img
        :src="imageUrl"
        alt=""
        class="CardTravelerPersona-imageElement"
        loading="lazy"
      >
    </div>

    <div class="CardTravelerPersona-overlay" aria-hidden="true" />

    <div class="CardTravelerPersona-body">
      <Tag
        class="CardTravelerPersona-tag"
        :type="tagType"
        :variant="tagVariant"
        size="md"
      >
        {{ tag }}
      </Tag>

      <div class="CardTravelerPersona-content">
        <div class="CardTravelerPersona-collapsible">
          <div class="CardTravelerPersona-fullContent">
            <div class="CardTravelerPersona-textGroup">
              <h5 class="CardTravelerPersona-title">
                {{ title }}
              </h5>
              <p v-if="description" class="CardTravelerPersona-description">
                {{ description }}
              </p>
            </div>

            <ul v-if="benefits?.length" class="CardTravelerPersona-benefits">
              <li
                v-for="benefit of benefits"
                :key="benefit"
                class="CardTravelerPersona-benefit"
              >
                <Icon
                  icon="check-circle"
                  size="sm"
                  class="CardTravelerPersona-benefitIcon"
                />
                <span>{{ benefit }}</span>
              </li>
            </ul>

            <ActionButton
              class="CardTravelerPersona-cta"
              variant="tertiary"
              :size="isMobile ? 'md' : 'lg'"
              rightIcon="arrow-right"
              @click="emit('click')"
            >
              {{ linkText }}
            </ActionButton>
          </div>
        </div>

        <p v-if="description" class="CardTravelerPersona-quote">
          {{ description }}
        </p>
      </div>
    </div>
  </Card>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useResponsive } from '@cvo/ui/composables';
import { ActionButton, Card, Icon, Tag } from '@cvo/ui/components';
import type { CardTravelerPersonaEmits, CardTravelerPersonaProps } from './CardTravelerPersona.model';

defineProps<CardTravelerPersonaProps>();

const emit = defineEmits<CardTravelerPersonaEmits>();

const isHovered = ref(false);
const { isDesktop, isMobile } = useResponsive();

const tagVariant = computed(() => (isHovered.value && isDesktop.value ? 'filled' : 'subtle'));
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  // Higher specificity (0-2-0) ensures these overrides win over .Card (0-1-0)
  .Card.CardTravelerPersona {
    padding: 0;
    overflow: hidden;
    background-color: transparent;
    border: none;
    border-radius: vars.$dimensions-d-4;
    position: relative;
    display: flex;
    flex-direction: column;

    @include responsive.mobile {
      background-color: vars.$organisms-card-contentcard-travelerpersonas-bg-color;
      border: vars.$dimensions-d-0-25 solid vars.$organisms-card-contentcard-travelerpersonas-border-color;
      border-radius: vars.$dimensions-d-3;
    }
  }

  .CardTravelerPersona {
    &-image {
      position: absolute;
      inset: 0;

      @include responsive.mobile {
        position: static;
        height: 110px;
        overflow: hidden;
      }
    }

    &-imageElement {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      pointer-events: none;
    }

    &-overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0) 50%,
        rgba(0, 0, 0, 0.8) 100%
      );
      pointer-events: none;

      @include responsive.mobile {
        display: none;
      }
    }

    &-body {
      position: relative;
      z-index: 1;
      display: flex;
      gap: vars.$dimensions-d-8;
      flex-direction: column;
      padding: vars.$dimensions-d-8;
      min-height: 400px;
      background-color: transparent;
      transition: background-color 0.3s ease;
      flex: 1;

      @include responsive.mobile {
        padding: vars.$dimensions-d-4;
        min-height: auto;
        gap: vars.$dimensions-d-3;
      }
    }

    &-tag {
      align-self: flex-start;
    }

    &-content {
      display: flex;
      flex-direction: column;
      flex: 1;
      position: relative;
    }

    &-collapsible {
      visibility: hidden;
      display: flex;
      flex: 1;

      @include responsive.mobile {
        visibility: visible;
      }
    }

    &-fullContent {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-8;
    }

    &-textGroup {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;
    }

    &-title {
      color: vars.$organisms-card-contentcard-travelerpersonas-title-color;
    }

    &-description {
      @include text.text(sm);
      color: vars.$organisms-card-contentcard-travelerpersonas-quote-color-hover;
    }

    &-benefits {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;
    }

    &-benefit {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-1;
      @include text.text(sm);
      color: vars.$atoms-benefit-text-color;
    }

    &-benefitIcon {
      flex-shrink: 0;
      color: vars.$atoms-benefit-icon-color;
    }

    &-cta {
      padding-inline: 0;
      margin-top: auto;
      align-self: flex-start;
    }

    &-quote {
      @include text.text(lg);
      color: vars.$organisms-card-contentcard-travelerpersonas-quote-color-default;
      margin-block-start: auto;
      position: absolute;
      bottom: 0;

      @include responsive.mobile {
        display: none;
      }
    }

    &:hover {
      .CardTravelerPersona-body {
        background-color: rgba(255, 255, 255, 0.8);
      }

      .CardTravelerPersona-collapsible {
        visibility: visible;
      }

      .CardTravelerPersona-quote {
        display: none;
      }
    }
  }
}
</style>
